import { CellState } from '../board';

// Fact addressing for the deduction graph.
//
// A *fact* is one atomic thing a solver can know about a cell:
//   elim(r,c)  — this cell cannot hold a queen
//   queen(r,c) — this cell holds a queen
//
// Facts are dense small integers so a whole solve state fits in a fixed-width bitset:
//   elim(r,c)  = r*N + c            in [0, N²)
//   queen(r,c) = N² + r*N + c       in [N², 2N²)
// With N ≤ 12 that is at most 288 bits — 9 Uint32 words (see bitset.ts).

export type FactId = number;

export const elimFact = (r: number, c: number, size: number): FactId => r * size + c;
export const queenFact = (r: number, c: number, size: number): FactId => size * size + r * size + c;
export const factCount = (size: number): number => 2 * size * size;

export const isQueenFact = (f: FactId, size: number): boolean => f >= size * size;

export function factRow(f: FactId, size: number): number {
  return Math.floor((f % (size * size)) / size);
}
export function factCol(f: FactId, size: number): number {
  return (f % (size * size)) % size;
}

/** "queen 3,4" / "elim 0,7" — for test failures and graph dumps. */
export function factLabel(f: FactId, size: number): string {
  return `${isQueenFact(f, size) ? 'queen' : 'elim'} ${factRow(f, size)},${factCol(f, size)}`;
}

/** The fact a cell's current mark asserts, or null if the cell is still blank. */
export function factOfCell(board: CellState[][], r: number, c: number): FactId | null {
  const size = board.length;
  const v = board[r][c];
  if (v === 'queen') return queenFact(r, c, size);
  if (v === 'X') return elimFact(r, c, size);
  return null;
}

/** Write a fact onto a board. Facts only ever add marks — the closure is monotone. */
export function applyFact(board: CellState[][], f: FactId): void {
  const size = board.length;
  board[factRow(f, size)][factCol(f, size)] = isQueenFact(f, size) ? 'queen' : 'X';
}
